import React from 'react';
import { Box, Button } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import FlightIcon from '@mui/icons-material/Flight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { trackButtonClick } from '~/utils/analytics';

export const menuItem = {
  visaItinerary: 'visaItinerary',
  searchFlight: 'searchFlight',
} as const;

export type MenuItemKey = keyof typeof menuItem;

type MenuConfig = Record<MenuItemKey, { to: string; label: string; icon: React.ElementType }>;

const route = {
  searchFlight: '/',
  cheapFlights: '/cheap-flights',
  visaItinerary: '/itinerary',
};

const menuConfig: MenuConfig = {
  visaItinerary: {
    to: route.visaItinerary,
    label: 'Request Visa Flight Itinerary',
    icon: DescriptionIcon,
  },
  searchFlight: {
    to: route.searchFlight,
    label: 'Search cheaper flights',
    icon: FlightIcon,
  },
};

type Props = {
  menuItems: MenuItemKey[];
};

const HeroMenuButtons: React.FC<Props> = ({ menuItems }) => {
  const location = useLocation();

  const isActive = (link: string): boolean => {
    if (link === route.visaItinerary) {
      return location.pathname.startsWith(route.visaItinerary);
    }

    if (link === route.searchFlight) {
      return location.pathname === route.searchFlight || location.pathname.startsWith(`${route.cheapFlights}/`);
    }

    return location.pathname === link;
  };

  return (
    <Box
      display="flex"
      justifyContent="left"
      flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
      gap={{ xs: 0, sm: 1.25 }}
      mt={2.5}
      sx={{
        width: { xs: '100%', sm: 'auto' },
        bgcolor: { xs: 'rgba(255, 255, 255, 0.12)', sm: 'transparent' },
        borderRadius: { xs: 1.5, sm: 0 },
        mb: { xs: 2.5, sm: 0 },
        overflow: 'visible',
      }}
    >
      {menuItems.map((item, index) => {
        const Icon = menuConfig[item].icon;
        const active = isActive(menuConfig[item].to);
        const isFirst = index === 0;
        const isLast = index === menuItems.length - 1;

        return (
          <Box
            key={item}
            sx={{
              position: 'relative',
              display: 'inline-flex',
              flex: { xs: 1, sm: 'initial' },
              minWidth: { xs: 0, sm: 'initial' },
              ...(active
                ? {
                  '&:hover .hero-menu-arrow': {
                    color: 'primary.dark',
                  },
                }
                : {}),
              '@media (max-width:599px)': {
                '&:last-child .MuiButton-root': {
                  borderRight: 0,
                },
              },
            }}
          >
            <Button
              component={RouterLink}
              to={menuConfig[item].to}
              variant={active ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              sx={{
                minWidth: { xs: 0, sm: 164 },
                width: { xs: '100%', sm: 'auto' },
                minHeight: { xs: 42, sm: 34 },
                borderRadius: {
                  xs: active
                    ? `${isFirst ? 6 : 0}px ${isLast ? 6 : 0}px ${isLast ? 6 : 0}px ${isFirst ? 6 : 0}px`
                    : 0,
                  sm: 999,
                },
                border: { xs: 0, sm: undefined },
                ...(!active
                  ? {
                    '@media (min-width:600px)': {
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 999,
                      '&:hover': {
                        border: '1px solid',
                        borderColor: 'primary.main',
                      },
                    },
                  }
                  : {}),
                px: { xs: 0.75, sm: 1.5 },
                py: { xs: 0.75, sm: 0.35 },
                fontSize: { xs: 10.5, sm: 12 },
                fontWeight: 700,
                lineHeight: { xs: 1.12, sm: 1.4 },
                letterSpacing: { xs: 0, sm: 0.1 },
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                textAlign: 'center',
                textTransform: 'none',
                '& .MuiButton-startIcon': {
                  mr: { xs: 0.4, sm: 0.75 },
                  ml: 0,
                  '& svg': {
                    fontSize: { xs: 16, sm: 17 },
                  },
                },
              }}
              startIcon={<Icon />}
              onClick={() => trackButtonClick(
                'HeroMenuButtons',
                menuConfig[item].label,
                `Hero Menu Button Clicked: ${menuConfig[item].label}`,
              )}
            >
              {menuConfig[item].label}
            </Button>
            {active && (
              <ArrowDropDownIcon
                className="hero-menu-arrow"
                sx={{
                  color: 'primary.main',
                  position: 'absolute',
                  display: 'block',
                  bottom: { xs: -22, sm: -23 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: { xs: 36, sm: 40 },
                  zIndex: 1,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default HeroMenuButtons;
